import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowUpRight,
  CalendarDays,
  Eye,
  Flame,
  Tags,
  TrendingUp,
  Users,
} from 'lucide-react';
import { canManageWorkspace } from '@/lib/permissions';
import { useI18n } from '@/hooks/useI18n';
import { useOverview, useOverviewSeries } from '@/hooks/queries/useOverview';
import { useSchedulerStatus, useSchedulerRuns } from '@/hooks/queries/useScheduler';
import { useTopAccounts } from '@/hooks/queries/useTopAccounts';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { QuickStat } from '@/components/primitives/QuickStat';
import { BarStack } from '@/components/primitives/BarStack';
import { Sparkline } from '@/components/primitives/Sparkline';
import { GetStartedTile } from '@/components/primitives/GetStartedTile';
import { Stagger } from '@/components/primitives/Stagger';
import { TextReveal } from '@/components/primitives/TextReveal';
import { Button } from '@/components/ui/button';
import { formatInt, formatNumber } from '@/lib/format';
import { formatAnalyticsBucket } from '@/lib/analyticsSeries';
import { normalizeQuotaRemaining } from '@/lib/scheduler';
import type { OverviewSeriesPoint } from '@/types/api';
import { ExecutionsActivity } from './ExecutionsActivity';
import { OverviewChart } from './OverviewChart';
import { GrowthBadge } from './GrowthBadge';

const PERIOD = 30;

export function OverviewScreen() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const overview = useOverview(PERIOD);
  const series = useOverviewSeries(PERIOD);
  const data = overview.data;
  const hasAccountBaseline =
    data?.accountsBaselineObservedAt != null && data.accountsCount7dAgo != null;
  const accountGrowthDescription = hasAccountBaseline
    ? t(
        `Variation du total des comptes suivis : ${formatInt(data?.totalAccounts)} aujourd’hui, contre ${formatInt(data?.accountsCount7dAgo)} lors du relevé du ${formatAnalyticsBucket(data?.accountsBaselineObservedAt ?? '', 'DAY', locale, true)}. Une variation en pourcentage ne se calcule pas depuis zéro.`,
        `Change in total tracked accounts: ${formatInt(data?.totalAccounts)} now, compared with ${formatInt(data?.accountsCount7dAgo)} in the observation on ${formatAnalyticsBucket(data?.accountsBaselineObservedAt ?? '', 'DAY', locale, true)}. A percentage cannot be calculated from zero.`,
      )
    : t(
        'Il n’existe pas encore de relevé comparable du nombre de comptes il y a 7 jours. La comparaison apparaîtra lorsqu’un relevé quotidien de référence datant de 7 jours sera disponible.',
        'There is no comparable account-count observation from 7 days ago yet. The comparison will appear once a daily reference observation from 7 days ago is available.',
      );
  const accountGrowthUnavailable = hasAccountBaseline
    ? data?.accountsCount7dAgo === 0 && (data?.totalAccounts ?? 0) > 0
      ? t('Nouveaux comptes', 'New accounts')
      : t('Comparaison indisponible', 'Comparison unavailable')
    : t('Historique en cours', 'Building history');
  const viewsGrowthDescription =
    data?.viewsGrowth7d != null
      ? t(
          `Vues gagnées sur les 7 derniers jours : ${formatInt(data.viewsDelta7d)}, contre ${formatInt(data.viewsDeltaPrevious7d)} sur les 7 jours précédents. Cette comparaison est indépendante du total sur 30 jours affiché au-dessus.`,
          `Views gained in the last 7 days: ${formatInt(data.viewsDelta7d)}, compared with ${formatInt(data.viewsDeltaPrevious7d)} in the preceding 7 days. This comparison is independent of the 30-day total shown above.`,
        )
      : t(
          'La comparaison exige des mesures exploitables sur deux périodes de 7 jours et une référence supérieure à zéro. Aucun pourcentage n’est estimé lorsque ces données manquent.',
          'The comparison requires usable measurements in both 7-day periods and a baseline above zero. No percentage is estimated when these data are missing.',
        );

  if (overview.error)
    return (
      <ErrorInline error={overview.error} onRetry={() => overview.refetch()} className="mt-4" />
    );

  return (
    <Stagger stagger={65} className="flex flex-col gap-6">
      <OverviewHeader />

      <section
        aria-label={t('Aperçu rapide', 'Quick overview')}
        className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2 xl:grid-cols-4"
      >
        <QuickStat
          label={t('Vues gagnées', 'Views gained')}
          hint={t('Somme des deltas de vues sur la période', 'Sum of view deltas over the period')}
          icon={<Eye className="size-4" />}
          subMeta={t('30 jours', '30 days')}
          value={formatNumber(data?.totalViewsDelta ?? 0)}
          suffix={
            <GrowthBadge
              value={data?.viewsGrowth7d}
              unavailableLabel={t('Comparaison indisponible', 'Comparison unavailable')}
              description={viewsGrowthDescription}
            />
          }
          loading={overview.isLoading}
          className="border-violet/20"
          chart={
            <BarStack values={pickValues(series.data?.viewsDelta)} palette="violet" height={32} />
          }
        />
        <QuickStat
          label={t('Abonnés', 'Followers')}
          hint={t('Total des dernières mesures connues', 'Total of latest known measurements')}
          icon={<TrendingUp className="size-4" />}
          subMeta={t('Total actuel', 'Current total')}
          value={formatNumber(data?.totalFollowers ?? 0)}
          loading={overview.isLoading}
          chart={
            <div className="h-8 text-violet">
              <Sparkline
                data={pickValues(series.data?.followers)}
                height={32}
                strokeWidth={2}
                fill="hsl(var(--violet) / 0.06)"
              />
            </div>
          }
        />
        <QuickStat
          label={t('Comptes suivis', 'Tracked accounts')}
          hint={t(
            'Nombre total de comptes actuellement suivis',
            'Total number of accounts currently tracked',
          )}
          icon={<Users className="size-4" />}
          subMeta={t('Total actuel', 'Current total')}
          value={formatNumber(data?.totalAccounts ?? 0)}
          loading={overview.isLoading}
          chart={
            <GrowthBadge
              value={data?.accountsGrowth7d}
              unavailableLabel={accountGrowthUnavailable}
              description={accountGrowthDescription}
            />
          }
        />
        <QuickStat
          label={t('Reels viraux', 'Viral reels')}
          hint={t(
            'Reels détectés comme viraux sur la période',
            'Reels detected as viral in this period',
          )}
          icon={<Flame className="size-4" />}
          subMeta={t('30 jours', '30 days')}
          value={formatNumber(data?.viralReelsCount ?? 0)}
          loading={overview.isLoading}
          chart={
            <BarStack values={pickValues(series.data?.viralReels)} palette="warm" height={32} />
          }
        />
      </section>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_270px]">
        {series.error ? (
          <ErrorInline error={series.error} onRetry={() => series.refetch()} />
        ) : (
          <OverviewChart data={series.data} loading={series.isLoading} />
        )}
        <QuickActions />
      </div>

      {user?.role === 'ADMIN' && <AdminActivity />}
    </Stagger>
  );
}

function OverviewHeader() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex min-w-0 flex-col justify-between gap-4 pb-1 sm:flex-row sm:items-center">
      <TextReveal className="min-w-0">
        <p className="t-stagger-line mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-deep">
          {t('Votre espace de pilotage', 'Your control center')}
        </p>
        <h1 className="t-stagger-line font-display text-[28px] leading-tight text-text-1 sm:text-[32px]">
          {t('Une vue d’ensemble.', 'The bigger picture.')}
        </h1>
        <p className="t-stagger-line t-stagger-line--2 mt-2 break-words text-[12px] leading-relaxed text-text-3 sm:text-[13px]">
          {t('Bon retour,', 'Welcome back,')}{' '}
          <span className="font-medium text-text-2">@{user?.username}</span>
          {t('. Voici l’essentiel de votre activité.', '. Here’s your activity at a glance.')}
        </p>
      </TextReveal>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <span className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-line-1 bg-bg-1 px-3 text-[11px] text-text-2">
          <CalendarDays className="size-3.5 text-text-3" aria-hidden="true" />
          {t('30 derniers jours', 'Last 30 days')}
        </span>
        <Button variant="primary" size="sm" onClick={() => navigate('/analytics')}>
          {t('Voir les analytics', 'View analytics')}
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function QuickActions() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <aside
      aria-label={t('Démarrage rapide', 'Quick start')}
      className="flex min-w-0 flex-col rounded-2xl border border-violet/15 bg-violet-soft/40 p-4"
    >
      <div className="relative hidden h-32 items-center justify-center xl:flex" aria-hidden="true">
        <img
          src="/images/analytics-sculpture.webp"
          alt=""
          width="768"
          height="768"
          className="studio-artwork size-44 object-contain"
        />
      </div>
      <div className="mb-4 px-1">
        <h2 className="text-[14px] font-semibold tracking-tight text-text-1">
          {t('Démarrage rapide', 'Quick start')}
        </h2>
        <p className="mt-1 text-[11px] leading-relaxed text-text-3">
          {t('Vos outils, à portée de main.', 'Your tools, right at hand.')}
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:grid sm:grid-cols-3 xl:flex">
        <GetStartedTile
          icon={<Users className="size-4" />}
          title={t('Comptes', 'Accounts')}
          subtitle={t('Ajouter un compte IG', 'Add an IG account')}
          onClick={() => navigate('/accounts')}
        />
        {canManageWorkspace(user?.role) && (
          <GetStartedTile
            icon={<Tags className="size-4" />}
            title={t('Identités', 'Identities')}
            subtitle={t('Créer une identité', 'Create an identity')}
            onClick={() => navigate('/identities')}
          />
        )}
        {user?.role === 'ADMIN' && (
          <GetStartedTile
            icon={<Activity className="size-4" />}
            title={t('Exploitation', 'Operations')}
            subtitle={t('Voir le scheduler', 'View scheduler')}
            onClick={() => navigate('/ops')}
          />
        )}
      </div>
    </aside>
  );
}

function AdminActivity() {
  const { t } = useI18n();
  const scheduler = useSchedulerStatus();
  const runs = useSchedulerRuns({ size: 10 });
  const topAccounts = useTopAccounts({ metric: 'VIEWS_GROWTH', periodDays: PERIOD, limit: 1 });
  const identities = useIdentities();
  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[16px] font-semibold tracking-tight text-text-1">
            {t('Activité d’exécution', 'Execution activity')}
          </h2>
          <p className="mt-1 text-[12px] text-text-3">
            {t('Dernières exécutions du scheduler.', 'Recent scheduler runs')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-3">
          <span>
            {t('Quota :', 'Quota:')}{' '}
            <span className="font-medium text-text-1">
              {formatNumber(normalizeQuotaRemaining(scheduler.data?.quotaRemaining))}
            </span>
          </span>
          <span className="h-3 w-px bg-line-2" aria-hidden="true" />
          <span>
            {t('En cours :', 'Running:')}{' '}
            <span className="font-medium text-text-1">{scheduler.data?.currentlyRunning ?? 0}</span>
          </span>
        </div>
      </header>
      <ExecutionsActivity
        runs={runs.data?.content ?? []}
        loading={runs.isLoading}
        topAccount={topAccounts.data?.[0]}
        identities={identities.data}
      />
    </section>
  );
}

function pickValues(points: OverviewSeriesPoint[] | undefined): number[] {
  return points?.map((point) => point.value) ?? [];
}
